import React, { useEffect, useState } from "react";
import Header from "../../components/Header/Header";
import { colors } from "../../constants/colors";
import * as yup from "yup";
import { Formik } from "formik";
import toast from "react-hot-toast";
import { Button, IconButton } from "@mui/material";
import {
  Check,
  CheckCircleOutline,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useDispatch } from "react-redux";
import { resetPassword } from "../../redux/user/userSlice";
import Input from "../../components/Input/CommonInput";
import logo from "../../assets/images/logo.svg";

const resetPasswordSchema = yup.object({
  password: yup
    .string()
    .required("Password is required.")
    .min(
      8,
      "Password is too short - should be 8 chars minimum with one uppercase, one lowercase and one special character"
    )
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+=\[\]{}|\\:;"'<>,.?/~`])[A-Za-z\d!@#$%^&*()_\-+=\[\]{}|\\:;"'<>,.?/~`]{8,}$/,
      "Password must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character."
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), ""], "Passwords must match")
    .required("Confirm Password is required."),
});

const resetPasswordValues = {
  password: "",
  confirmPassword: "",
};

const ResetPassword = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [search] = useSearchParams();
  const [token, setToken] = useState("");
  const [isUpdated, setIsUpdated] = useState(false);

  useEffect(() => {
    const searchToken = search.get("token");
    if (searchToken) {
      setToken(searchToken);
    }
  }, [search]);

  return (
    <div className="flex flex-col items-center justify-center w-full bg-white min-h-screen">
      <div className="absolute top-1 left-1">
        <Link to="/">
          <img
            src={logo}
            alt="Logo"
            className="ml-10 w-[90px] h-[90px] md:w-[120px] md:h-[120px] object-contain float-left"
          />
        </Link>
      </div>
      <div className="flex flex-col items-center justify-center w-full px-5 py-8 md:px-14 md:py-12 shadow-xs min-h-screen">
        <div className="flex flex-col items-center justify-center w-full gap-[31px] max-w-[962px]">
          {!isUpdated ? (
            <>
              <h4 className="text-lg md:text-3xl">Reset Password</h4>
              <Formik
                initialValues={resetPasswordValues}
                validationSchema={resetPasswordSchema}
                onSubmit={async (values, { setSubmitting }) => {
                  if (token) {
                    const response = await dispatch(
                      resetPassword({
                        password: values.password,
                        token,
                      })
                    );
                    if (response?.error?.message) {
                      toast.error(response?.payload?.message);
                      return;
                    }
                    setIsUpdated(true);
                  }
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  isValid,
                  setFieldValue,
                  /* and other goodies */
                }) => (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!isValid && errors.password) {
                        toast.error(errors.password);
                        return;
                      }
                      if (!isValid && errors.confirmPassword) {
                        toast.error(errors.confirmPassword);
                        return;
                      }
                      handleSubmit();
                    }}
                    className="w-full flex flex-col gap-[31px]"
                  >
                    <>
                      <div className="flex flex-col items-start justify-start w-[48%] mx-auto gap-2">
                        <p className="text-center text-white text-sm md:text-lg">
                          Password
                        </p>
                        <Input
                          label="Password"
                          id="password"
                          name="password"
                          type={passwordVisible ? "text" : "password"}
                          onChange={handleChange("password")}
                          suffix={
                            <IconButton
                              onClick={() =>
                                setPasswordVisible(!passwordVisible)
                              }
                            >
                              {passwordVisible ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          }
                          value={values.password}
                          className="w-full bg-white"
                        />
                      </div>
                      <div className="flex flex-col justify-start items-center w-full gap-[42px]">
                        <div className="flex flex-col items-start justify-start w-[48%] mx-auto gap-2">
                          <Input
                            label="Confirm Password"
                            name="confirmPassword"
                            type="password"
                            onChange={handleChange("confirmPassword")}
                            value={values.confirmPassword}
                            className="w-full bg-white"
                            id="confirmPassword"
                          />
                        </div>
                        <p className="text-textGrey text-center text-sm max-w-[460px] leading-5">
                          A combination of uppercase letters, lowercase
                          letters,numbers, and symbols. At least 8 characters
                          long but 12 or more is better.
                        </p>
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          sx={{
                            backgroundColor: "#255288",
                            maxWidth: 400,
                            "&:hover": { backgroundColor: "#F17A21" },
                          }}
                        >
                          Reset Password
                        </Button>
                      </div>
                    </>
                  </form>
                )}
              </Formik>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-y-2 mt-5 gap-4">
                <CheckCircleOutline
                  style={{ fontSize: "8rem", color: "green" }}
                />
              </div>
              <div className="flex flex-col gap-y-8 items-center justify-center">
                <p className="font-inter text-2xl">
                  Your password has been updated successfully{" "}
                </p>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    backgroundColor: "#255288",
                    "&:hover": { backgroundColor: "#F17A21" },
                  }}
                  onClick={() => navigate("/")}
                >
                  <span className="text-sm md:text-lg">Login to Continue</span>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
